// Security dashboard, WordPress, database monitoring, geolocation, KEV, exploit validation, MITRE ATT&CK API
import { api } from './client';

// ============================================================================
// SECURITY DASHBOARD API
// ============================================================================

export const securityDashboardAPI = {
  // Dashboard Overview
  getOverview: (timeRange: string = '7d') => {
    console.log('🛡️ Fetching security dashboard overview for timeRange:', timeRange);
    return api.get(`/security/dashboard?timeRange=${timeRange}`);
  },

  // Platform Analytics (comprehensive statistics)
  getPlatformAnalytics: (params?: {
    client_id?: string | number;
    time_range?: string;
    start_date?: string;
    end_date?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.client_id) searchParams.append('client_id', String(params.client_id));
    if (params?.time_range) searchParams.append('time_range', params.time_range);
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';
    console.log('📊 Fetching platform analytics', queryString);
    return api.get(`/security/platform-analytics${queryString}`);
  },

  // Security Alerts
  getAlerts: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('🚨 Fetching security alerts', queryString);
    return api.get(`/security/alerts${queryString}`);
  },

  acknowledgeAlert: (alertId: number) => {
    console.log('✅ Acknowledging security alert:', alertId);
    return api.post(`/security/alerts/${alertId}/acknowledge`);
  },

  resolveAlert: (alertId: number, notes: string) => {
    console.log('✅ Resolving security alert:', alertId, 'with notes:', notes);
    return api.post(`/security/alerts/${alertId}/resolve`, { notes });
  },

  // Vulnerabilities
  getRecentVulnerabilities: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('🔍 Fetching recent vulnerabilities', queryString);
    return api.get(`/security/vulnerabilities${queryString}`);
  },

  // Threat Landscape
  getThreatLandscape: (timeRange: string = '30d') => {
    console.log('🌍 Fetching threat landscape for timeRange:', timeRange);
    return api.get(`/security/threat-landscape?timeRange=${timeRange}`);
  },

  // Security News
  getSecurityNews: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('📰 Fetching security news', queryString);
    return api.get(`/security/news${queryString}`);
  },

  // Security Feeds Management
  getFeeds: () => {
    console.log('📡 Fetching security feeds');
    return api.get('/security/feeds');
  },

  updateFeed: (feedName: string) => {
    console.log('🔄 Updating security feed:', feedName);
    return api.post(`/security/feeds/${feedName}/update`);
  },

  updateAllFeeds: () => {
    console.log('🔄 Updating all security feeds');
    return api.post('/security/feeds/update-all');
  },

  // Alert Details and Management
  getAlertDetails: (alertId: number) => {
    console.log('🚨 Fetching alert details:', alertId);
    return api.get(`/security/alerts/${alertId}`);
  },

  assignAlert: (alertId: number, userId: number, notes: string) => {
    console.log('👤 Assigning alert:', alertId, 'to user:', userId);
    return api.post(`/security/alerts/${alertId}/assign`, { user_id: userId, notes });
  },

  getAlertActions: (alertId: number) => {
    console.log('📋 Fetching alert actions:', alertId);
    return api.get(`/security/alerts/${alertId}/actions`);
  },

  // Vulnerability Details and Browsing
  getVulnerabilityDetails: (vulnId: number) => {
    console.log('🐛 Fetching vulnerability details:', vulnId);
    return api.get(`/security/vulnerabilities/${vulnId}`);
  },

  browseVulnerabilities: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('🔍 Browsing vulnerabilities', queryString);
    return api.get(`/security/vulnerabilities/browse${queryString}`);
  },

  // Threat Intelligence
  getThreatIntelligence: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('🎯 Fetching threat intelligence', queryString);
    return api.get(`/security/threat-intel${queryString}`);
  },

  getThreatIntelDetails: (threatId: number) => {
    console.log('🎯 Fetching threat intelligence details:', threatId);
    return api.get(`/security/threat-intel/${threatId}`);
  },

  // Feed Details
  getFeedDetails: (feedName: string) => {
    console.log('📡 Fetching feed details:', feedName);
    return api.get(`/security/feeds/${feedName}`);
  },

  // News Details
  getNewsDetails: (newsId: number) => {
    console.log('📰 Fetching news details:', newsId);
    return api.get(`/security/news/${newsId}`);
  },

  // Security Metrics
  getSecurityMetrics: (timeRange: string = '7d') => {
    console.log('📊 Fetching security metrics for timeRange:', timeRange);
    return api.get(`/security/metrics?timeRange=${timeRange}`);
  },

  // Risk Assessment
  getRiskAssessment: () => {
    console.log('⚖️ Fetching risk assessment');
    return api.get('/security/risk-assessment');
  },

  // Security Configuration
  getDashboardConfig: () => {
    console.log('⚙️ Fetching security dashboard configuration');
    return api.get('/security/dashboard/config');
  },

  updateDashboardConfig: (config: any) => {
    console.log('💾 Updating security dashboard configuration:', config);
    return api.put('/security/dashboard/config', config);
  },

  // Project Risk Cards
  getProjectRiskCards: () => {
    console.log('📊 Fetching project risk cards');
    return api.get('/security/project-risk-cards');
  },

  // Project Risk Grade History (for trending charts)
  getProjectRiskHistory: (projectId: number, limit: number = 20) => {
    return api.get(`/security/projects/${projectId}/risk-history?limit=${limit}`);
  },

  // 24-Hour Incident Report
  get24HIncidentReport: () => {
    console.log('📋 Fetching 24-hour incident report');
    return api.get('/security/incident-report-24h');
  },

  // Top 5 This Week
  getTop5ThisWeek: () => {
    console.log('🔥 Fetching top 5 this week');
    return api.get('/security/top5-this-week');
  },
};

// ============================================================================
// WORDPRESS DASHBOARD API
// ============================================================================

export const wordPressDashboardAPI = {
  getDashboard: () => {
    console.log('🔧 Fetching WordPress dashboard data');
    return api.get('/wordpress/dashboard');
  },

  getVulnerabilities: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('🐛 Fetching WordPress vulnerabilities', queryString);
    return api.get(`/wordpress/vulnerabilities${queryString}`);
  },

  getVulnerability: (id: string) => {
    console.log('🐛 Fetching WordPress vulnerability:', id);
    return api.get(`/wordpress/vulnerabilities/${id}`);
  },

  getStatistics: (days: number = 30) => {
    console.log('📊 Fetching WordPress statistics for', days, 'days');
    return api.get(`/wordpress/stats?days=${days}`);
  },

  getFeeds: () => {
    console.log('📡 Fetching WordPress feeds');
    return api.get('/wordpress/feeds');
  },

  updateFeed: (feedId: number, data: any) => {
    console.log('📝 Updating WordPress feed:', feedId);
    return api.put(`/wordpress/feeds/${feedId}`, data);
  },

  startFeedUpdate: (data: any) => {
    console.log('🔄 Starting WordPress feed update:', data);
    return api.post('/wordpress/feeds/update', data);
  },

  getFeedUpdateStatus: (updateId: number) => {
    console.log('📊 Fetching feed update status:', updateId);
    return api.get(`/wordpress/feed-updates/${updateId}/status`);
  },

  getPlugins: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('🔌 Fetching WordPress plugins', queryString);
    return api.get(`/wordpress/plugins${queryString}`);
  },

  updatePluginData: (slug: string) => {
    console.log('🔄 Updating plugin data for:', slug);
    return api.post(`/wordpress/plugins/${slug}/update`);
  },

  getConfig: () => {
    console.log('⚙️ Fetching WordPress configuration');
    return api.get('/wordpress/config');
  },

  updateConfig: (config: any) => {
    console.log('💾 Updating WordPress configuration:', config);
    return api.put('/wordpress/config', config);
  },

  updateStatistics: () => {
    console.log('📊 Manually updating WordPress statistics');
    return api.post('/wordpress/stats/update');
  },
};

// ============================================================================
// DATABASE MONITORING API
// ============================================================================

export const databaseMonitoringAPI = {
  getDatabaseSize: () => {
    console.log('💾 Fetching database size information');
    return api.get('/database/size');
  },

  getIndexHealth: () => {
    console.log('🔍 Fetching database index health');
    return api.get('/database/index-health');
  },

  getPerformanceMetrics: () => {
    console.log('📊 Fetching database performance metrics');
    return api.get('/database/performance');
  },

  getHealthScore: () => {
    console.log('💯 Fetching database health score');
    return api.get('/database/health-score');
  },

  getMaintenanceRecommendations: () => {
    console.log('🔧 Fetching database maintenance recommendations');
    return api.get('/database/maintenance');
  },

  getMonitoringDashboard: () => {
    console.log('📈 Fetching comprehensive database monitoring dashboard');
    return api.get('/database/dashboard');
  },

  getStorageAnalysis: () => {
    console.log('💽 Fetching database storage analysis');
    return api.get('/database/storage-analysis');
  },

  getCapacityPlanning: () => {
    console.log('📊 Fetching database capacity planning data');
    return api.get('/database/capacity-planning');
  },

  executeMaintenanceTask: (taskData: any) => {
    console.log('🔧 Executing database maintenance task:', taskData);
    return api.post('/database/maintenance/execute', taskData);
  },
};

// ============================================================================
// IP GEOLOCATION API
// ============================================================================

export const geolocationAPI = {
  lookupIP: (ip: string) => {
    console.log('🌍 Looking up IP geolocation:', ip);
    return api.get(`/geolocation/lookup/${ip}`);
  },

  lookupBatch: (ips: string[]) => {
    console.log('🌍 Batch IP geolocation lookup:', ips.length, 'IPs');
    return api.post('/geolocation/lookup', { ips });
  },

  getScanMap: (scanId: number) => {
    console.log('🗺️ Getting scan map data:', scanId);
    return api.get(`/geolocation/scan/${scanId}/map`);
  },

  getProjectMap: (projectId: number) => {
    console.log('🗺️ Getting project map data:', projectId);
    return api.get(`/geolocation/project/${projectId}/map`);
  },

  getReconScanMap: (scanId: number) => {
    console.log('🗺️ Getting recon scan map data:', scanId);
    return api.get(`/geolocation/recon/${scanId}/map`);
  },

  getStats: () => {
    console.log('📊 Getting geolocation cache stats');
    return api.get('/geolocation/stats');
  },

  clearCache: () => {
    console.log('🗑️ Clearing geolocation cache');
    return api.post('/geolocation/cache/clear');
  },
};

// ============================================================================
// CISA KEV API
// ============================================================================

export const kevAPI = {
  syncCatalog: () => {
    console.log('🔄 Syncing CISA KEV catalog');
    return api.post('/kev/sync');
  },

  getStats: () => {
    console.log('📊 Fetching KEV statistics');
    return api.get('/kev/stats');
  },

  getList: (page: number = 1, limit: number = 50) => {
    console.log('📋 Fetching KEV list, page:', page);
    return api.get(`/kev/list?page=${page}&limit=${limit}`);
  },

  checkCVEs: (cveIds: string[]) => {
    console.log('🔍 Checking CVEs against KEV catalog:', cveIds.length);
    return api.post('/kev/check', { cve_ids: cveIds });
  },

  enrichScan: (scanId: number) => {
    console.log('✨ Enriching scan with KEV data:', scanId);
    return api.post(`/kev/enrich/${scanId}`);
  },

  getCVE: (cveId: string) => {
    console.log('🔍 Looking up CVE in KEV:', cveId);
    return api.get(`/kev/cve/${cveId}`);
  },

  getRansomware: () => {
    console.log('🦠 Fetching ransomware-linked KEV entries');
    return api.get('/kev/ransomware');
  },
};

// ============================================================================
// EXPLOIT VALIDATION API
// ============================================================================

export interface ExploitModule {
  id: string;
  name: string;
  cve: string;
  cvss_score: number;
  category: string;
  affected_product: string;
  severity: string;
  description: string;
  detection: string;
  tags: string[];
  references: string[];
}

export interface ExploitValidationResult {
  finding_id: number;
  validated: boolean;
  exploitable: boolean;
  confirmed?: boolean;
  confidence?: number;
  exploit_proof?: string;
  message: string;
  validated_at: string;
  validation_result?: any;
}

export const exploitValidationAPI = {
  getModules: () => {
    console.log('🔍 Fetching exploit validation modules');
    return api.get('/exploit-validation/modules');
  },

  getModulesByCategory: (category: string) => {
    console.log('🔍 Fetching exploit modules by category:', category);
    return api.get(`/exploit-validation/modules/category/${category}`);
  },

  getCategories: () => {
    console.log('📂 Fetching exploit module categories');
    return api.get('/exploit-validation/categories');
  },

  getStats: () => {
    console.log('📊 Fetching exploit validation statistics');
    return api.get('/exploit-validation/stats');
  },

  validateScan: (scanId: number) => {
    console.log('⚡ Running exploit validation for scan:', scanId);
    return api.post(`/exploit-validation/scan/${scanId}/validate`);
  },

  validateTarget: (targetId: number) => {
    console.log('⚡ Running exploit validation for target:', targetId);
    return api.post(`/exploit-validation/target/${targetId}/validate`);
  },

  validateIP: (ip: string, hostname?: string, ports?: number[]) => {
    console.log('⚡ Running exploit validation for IP:', ip);
    return api.post('/exploit-validation/ip', { ip, hostname, ports });
  },

  validateFinding: (findingId: number) => {
    console.log('⚡ Running exploit validation for finding:', findingId);
    return api.post<ExploitValidationResult>(`/exploit-validation/finding/${findingId}/validate`);
  },
};

// ============================================================================
// MITRE ATT&CK API
// ============================================================================

export interface FindingDetail {
  id: number;
  host: string;
  ip: string;
  port: number;
  title: string;
  severity: string;
  cve_id?: string;
  service?: string;
}

export interface TacticInfo {
  id: string;
  name: string;
  color: string;
  techniques_detected: number;
}

export interface AttackMapping {
  technique_id: string;
  technique_name: string;
  tactic: string;
  severity: string;
  findings_count: number;
  findings?: FindingDetail[];
  reasoning?: string;
  atomic_tests?: AtomicTestSuggestion[];
  description?: string;
}

export interface AtomicTestSuggestion {
  name: string;
  guid: string;
  description: string;
  platform: string;
  command?: string;
  run_with: string;
}

export interface AttackPlaybook {
  id: string;
  name: string;
  description: string;
  techniques: AttackMapping[];
  recommendations: string[];
}

export interface AtomicTest {
  name: string;
  description: string;
  supported_platforms: string[];
  executor: string;
  command?: string;
}

export interface AttackMappingsResponse {
  scan_id: number;
  total_results: number;
  total_mappings: number;
  techniques_found: number;
  mappings: AttackMapping[];
  all_tactics: TacticInfo[];
}

export const mitreAPI = {
  getAttackMappings: (scanId: number) => {
    console.log('🎯 Fetching MITRE ATT&CK mappings for scan:', scanId);
    return api.get<AttackMappingsResponse>(`/scans/${scanId}/attack-mappings`);
  },

  getAttackPlaybook: (scanId: number) => {
    console.log('📋 Fetching attack playbook for scan:', scanId);
    return api.get<AttackPlaybook>(`/scans/${scanId}/attack-playbook`);
  },

  getAttackSummary: (scanId: number) => {
    console.log('📊 Fetching attack summary for scan:', scanId);
    return api.get(`/scans/${scanId}/attack-summary`);
  },

  getAttackSuggestions: (resultId: number) => {
    console.log('💡 Fetching attack suggestions for result:', resultId);
    return api.get(`/results/${resultId}/attack-suggestions`);
  },

  getAtomicTests: (techniqueId: string) => {
    console.log('🧪 Fetching atomic tests for technique:', techniqueId);
    return api.get(`/mitre/atomic-tests/${techniqueId}`);
  },

  exportAttackMappingsHTML: (scanId: number) => {
    console.log('📥 Exporting MITRE ATT&CK HTML report for scan:', scanId);
    return api.get(`/scans/${scanId}/attack-mappings/export`, {
      responseType: 'blob',
    });
  },

  exportAttackMappingsPDF: (scanId: number) => {
    console.log('Exporting MITRE ATT&CK PDF report for scan:', scanId);
    return api.get(`/scans/${scanId}/attack-mappings/export-pdf`, {
      timeout: 180000,
    });
  },
};

// ============================================================================
// ATTACK SURFACE & CORRELATION API
// ============================================================================

export interface AttackSurfaceScore {
  overall_score: number;
  risk_level: 'critical' | 'high' | 'medium' | 'low';
  trend: 'improving' | 'worsening' | 'stable';
  change_30d: number;
  last_calculated: string;
  breakdown: {
    exposed_services: ComponentScore;
    vulnerable_assets: ComponentScore;
    shadow_it: ComponentScore;
    certificate_issues: ComponentScore;
    breach_exposure: ComponentScore;
    high_value_targets: ComponentScore;
  };
  top_risks: AttackSurfaceRisk[];
  recommendations: string[];
}

export interface ComponentScore {
  score: number;
  weight: number;
  count: number;
  description: string;
}

export interface AttackSurfaceRisk {
  category: string;
  title: string;
  description: string;
  severity: string;
  impact: number;
  asset_count: number;
}

export interface CorrelationInsight {
  type: string;
  severity: string;
  title: string;
  description: string;
  impact: string;
  asset_id?: number;
  result_id?: number;
}

export interface TimelineEvent {
  date: string;
  event_type: string;
  title: string;
  description: string;
  asset_id?: number;
  source: string;
}

export interface AmplificationFactor {
  factor: string;
  description: string;
  impact: number;
}

export interface AmplifiedFinding {
  id: number;
  title: string;
  severity: string;
  host: string;
  port: number;
  service: string;
  cve_id: string;
  original_cvss: number;
  amplified_cvss: number;
  cvss_increase: number;
  cvss_increase_percent: number;
  amplification_factors: AmplificationFactor[];
  days_exposed: number;
  exposure_days: number;
  correlation_type: string;
  source: string;
  sources: string[];
  confidence: number;
  first_seen?: string;
}

export const attackSurfaceAPI = {
  getAttackSurfaceScore: (projectId: number) => {
    console.log('🎯 Fetching attack surface score for project:', projectId);
    return api.get<AttackSurfaceScore>(`/recon/projects/${projectId}/attack-surface`);
  },

  getCorrelationInsights: (projectId: number) => {
    console.log('💡 Fetching correlation insights for project:', projectId);
    return api.get<{ insights: CorrelationInsight[]; count: number }>(`/recon/projects/${projectId}/insights`);
  },

  getExposureTimeline: (projectId: number, limit: number = 50) => {
    console.log('📅 Fetching exposure timeline for project:', projectId);
    return api.get<{ events: TimelineEvent[]; count: number }>(`/recon/projects/${projectId}/timeline?limit=${limit}`);
  },

  getCorrelationSummary: (projectId: number) => {
    console.log('📊 Fetching correlation summary for project:', projectId);
    return api.get<{
      total_recon: number;
      total_active: number;
      confirmed: number;
      passive_only: number;
      total: number;
    }>(`/recon/projects/${projectId}/correlation-summary`);
  },

  getAmplifiedFindings: (projectId: number) => {
    console.log('📈 Fetching amplified findings for project:', projectId);
    return api.get<{
      findings: AmplifiedFinding[];
      count: number;
      recon_scan_id: number;
    }>(`/recon/projects/${projectId}/amplified-findings`);
  },
};

// ============================================================================
// THREAT INTELLIGENCE / DETECTION COVERAGE API
// ============================================================================

export const threatIntelAPI = {
  getCapabilities: (params?: { category?: string; active?: string }) => {
    const qs = params ? new URLSearchParams(params as Record<string, string>).toString() : '';
    return api.get(`/admin/threat-intelligence/capabilities${qs ? '?' + qs : ''}`);
  },
  getCapability: (id: number) => api.get(`/admin/threat-intelligence/capabilities/${id}`),
  createCapability: (data: Record<string, any>) => api.post('/admin/threat-intelligence/capabilities', data),
  updateCapability: (id: number, data: Record<string, any>) => api.put(`/admin/threat-intelligence/capabilities/${id}`, data),
  deleteCapability: (id: number) => api.delete(`/admin/threat-intelligence/capabilities/${id}`),
  getCapabilityStats: () => api.get('/admin/threat-intelligence/capabilities-stats'),
  getCategories: () => api.get('/admin/threat-intelligence/capabilities-categories'),

  getPendingDetections: (params?: { status?: string; priority?: string; source?: string; product?: string; overdue?: string }) => {
    const qs = params ? new URLSearchParams(params as Record<string, string>).toString() : '';
    return api.get(`/admin/threat-intelligence/pending${qs ? '?' + qs : ''}`);
  },
  getPendingDetection: (id: number) => api.get(`/admin/threat-intelligence/pending/${id}`),
  createPendingDetection: (data: Record<string, any>) => api.post('/admin/threat-intelligence/pending', data),
  updatePendingDetection: (id: number, data: Record<string, any>) => api.put(`/admin/threat-intelligence/pending/${id}`, data),
  deletePendingDetection: (id: number) => api.delete(`/admin/threat-intelligence/pending/${id}`),
  getPendingStats: () => api.get('/admin/threat-intelligence/pending-stats'),

  triggerAnalysis: () => api.post('/admin/threat-intelligence/analyze'),

  // Scanner coverage from test_definitions
  getScannerCoverage: () => api.get('/test-definitions/metadata'),
};
